import { createContext, useContext, useState, useEffect, useRef } from 'react';

const FavoritesContext = createContext();

export const FavoritesProvider = ({ children }) => {
  // Use lazy initializer to load from localStorage synchronously (StrictMode-safe)
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem('ikonic_favorites');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Error loading favorites:', e);
      return [];
    }
  });
  
  // Track if this is the initial mount to avoid writing empty array on first render
  const isInitialMount = useRef(true);

  // Save to localStorage whenever favorites change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    localStorage.setItem('ikonic_favorites', JSON.stringify(favorites));
  }, [favorites]);

  const addFavorite = (property) => {
    setFavorites(prev => {
      if (prev.some(p => p.id === property.id)) {
        return prev;
      }
      return [...prev, {
        id: property.id,
        slug: property.slug,
        name: property.name,
        price_usd: property.price_usd,
        transaction_type: property.transaction_type,
        property_type: property.property_type,
        zone: property.zone,
        department: property.department,
        images: property.images?.slice(0, 1) || [],
        bedrooms: property.bedrooms,
        bathrooms: property.bathrooms,
        area_m2: property.area_m2,
        addedAt: new Date().toISOString()
      }];
    });
  };

  const removeFavorite = (propertyId) => {
    setFavorites(prev => prev.filter(p => p.id !== propertyId));
  };

  const isFavorite = (propertyId) => {
    return favorites.some(p => p.id === propertyId);
  };

  const toggleFavorite = (property) => {
    if (isFavorite(property.id)) {
      removeFavorite(property.id);
    } else {
      addFavorite(property);
    }
  };

  const clearFavorites = () => {
    setFavorites([]);
  };

  return (
    <FavoritesContext.Provider value={{
      favorites,
      addFavorite,
      removeFavorite,
      isFavorite,
      toggleFavorite,
      clearFavorites,
      favoritesCount: favorites.length
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
